/**
 * useSeanceLive.js — Persistance localStorage d'une séance live par code REMC.
 *
 * Approche : la source de vérité est localStorage. Le state React est un
 * miroir synchrone, mis à jour à chaque action. Les actions lisent
 * directement le storage pour éviter les stale closures.
 *
 * Stocke par code (ex C1-g) :
 *  - briefingNotes  : string
 *  - guidageNotes   : string
 *  - critereStatuts : { critereIndex: 'ok' | 'ko' | null }
 *  - passages       : number
 *  - startedAt      : ISO timestamp
 *  - updatedAt      : ISO timestamp
 */
import { useCallback, useMemo, useState } from 'react';

const STORAGE_KEY = 'c2r:seance-live:v1';

function readAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

function writeAll(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

const emptyState = () => ({
  briefingNotes: '',
  guidageNotes: '',
  critereStatuts: {},
  passages: 0,
  startedAt: null,
  updatedAt: null,
});

function loadForCode(code) {
  if (!code) return emptyState();
  const all = readAll();
  return all[code] || emptyState();
}

export default function useSeanceLive(code) {
  // Compteur incrémenté à chaque mutation pour forcer un re-render.
  // Les valeurs proviennent toujours de localStorage (source de vérité).
  const [version, setVersion] = useState(0);
  const [activeCode, setActiveCode] = useState(code);

  // Si le code passe en props change, on remet le compteur à zéro
  // et on signale qu'on lit pour ce nouveau code.
  if (activeCode !== code) {
    setActiveCode(code);
    setVersion((v) => v + 1);
  }

  const state = useMemo(() => loadForCode(code), [code, version]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = useCallback((mutator) => {
    if (!code) return;
    const all = readAll();
    const prev = all[code] || emptyState();
    const next = {
      ...mutator(prev),
      startedAt: prev.startedAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    all[code] = next;
    writeAll(all);
    setVersion((v) => v + 1);
  }, [code]);

  const updateField = useCallback((field, value) => {
    commit((prev) => ({ ...prev, [field]: value }));
  }, [commit]);

  const setCritereStatut = useCallback((index, statut) => {
    commit((prev) => {
      const newStatuts = { ...prev.critereStatuts };
      if (statut === null || newStatuts[index] === statut) {
        delete newStatuts[index];
      } else {
        newStatuts[index] = statut;
      }
      return { ...prev, critereStatuts: newStatuts };
    });
  }, [commit]);

  const incrementPassages = useCallback((delta = 1) => {
    commit((prev) => ({ ...prev, passages: Math.max(0, prev.passages + delta) }));
  }, [commit]);

  const reset = useCallback(() => {
    if (!code) return;
    const all = readAll();
    delete all[code];
    writeAll(all);
    setVersion((v) => v + 1);
  }, [code]);

  // Stats calculées
  const stats = useMemo(() => {
    const statuts = Object.values(state.critereStatuts);
    const okCount = statuts.filter((s) => s === 'ok').length;
    const koCount = statuts.filter((s) => s === 'ko').length;
    const totalTested = okCount + koCount;
    const okRatio = totalTested > 0 ? Math.round((okCount / totalTested) * 100) : null;
    return { okCount, koCount, totalTested, okRatio };
  }, [state]);

  return {
    state,
    stats,
    updateField,
    setCritereStatut,
    incrementPassages,
    reset,
  };
}

/**
 * Liste toutes les séances live en cours (pour un éventuel historique).
 */
export function listSeancesLive() {
  const all = readAll();
  return Object.entries(all)
    .map(([code, data]) => ({ code, ...data }))
    .sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
}
